// The embedded web app's eye on its own location: a navigation that
// reaches a screen the iPhone app draws itself (Today, a chat, Meetings …)
// is handed to the app and undone here, so the web screen stays where the
// user was while the app opens its own screen over it. The rules and the
// memory are in linkBridge.ts; this is the React half. Mounted once, inside
// the customer router, only when the page is embedded (App.tsx).

import { useEffect, useSyncExternalStore } from "react";
import { useLocation, useNavigate, type Location, type NavigateFunction } from "react-router-dom";

import { canPostToNative, isEmbedded, postToNative } from "./embedded";
import { createBridgeMemory, type BridgeUndo } from "./linkBridge";

// react-router keeps its own index (history.state.idx, 0 for the entry the
// page booted on) and key in each entry's history state.
function historyState(): { idx?: unknown; key?: unknown } | null {
  return typeof window === "undefined" ? null : (window.history.state as { idx?: unknown; key?: unknown } | null);
}

// The key react-router gives the entry the page loaded on: its own key from
// history.state on a reload, "default" on a fresh load. Read when this
// module loads (main.tsx imports it through App before anything renders or
// navigates), so the very first render already knows the boot entry: a
// reload at index 2 renders its page instead of hiding it as a handoff.
function bootKey(): string | null {
  if (typeof window === "undefined") return null;
  const key = historyState()?.key;
  return typeof key === "string" && key ? key : "default";
}

// One router per page, so one memory. Module state rather than a ref: the
// embedded shell reads it while it renders (useNativeHandoff).
const bridge = createBridgeMemory(bootKey(), {
  now: () => Date.now(),
  index: () => {
    const idx = historyState()?.idx;
    return typeof idx === "number" ? idx : null;
  },
  canPost: canPostToNative,
});

// The shell re-renders when a hidden screen may show again: a handoff that
// never landed lapses into rendering the page here (linkBridge SETTLE_MS),
// or the message could not be posted at all.
let version = 0;
const listeners = new Set<() => void>();
function notify(): void {
  version += 1;
  listeners.forEach((listener) => listener());
}
function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
const getVersion = () => version;

/**
 * True while `location` is a screen this page is handing to the app, or to
 * Safari: the embedded shell renders nothing for it, so the web copy of
 * that screen neither flashes nor starts loading. Decided once per
 * navigation, so a re-render never flips it.
 */
export function useNativeHandoff(location: Location): boolean {
  useSyncExternalStore(subscribe, getVersion, getVersion);
  const until = isEmbedded() ? bridge.hiddenUntil(location) : 0;
  useEffect(() => {
    if (!until) return;
    const timer = window.setTimeout(notify, Math.max(0, until - Date.now()) + 1);
    return () => window.clearTimeout(timer);
  }, [until]);
  return until > 0;
}

function undo(navigate: NavigateFunction, then: BridgeUndo): void {
  if (then.kind === "back") {
    navigate(-1);
    return;
  }
  const { pathname, search, hash, state } = then.to;
  navigate({ pathname, search, hash }, { replace: true, state });
}

export function NativeLinkBridge() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const action = bridge.arrive(location);
    switch (action.kind) {
      case "none":
        return;
      case "post":
        if (postToNative(action.message)) {
          undo(navigate, action.then);
        } else {
          bridge.release(location);
          notify();
        }
        return;
      case "undo":
        undo(navigate, action.then);
        return;
      case "load":
        // A real load, which WebKit asks the app's navigation policy about:
        // a Safari-only path opens in Safari and this page stays as it is.
        window.location.assign(action.href);
        return;
    }
  }, [location, navigate]);

  return null;
}
